import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  useCallback,
} from "react";
import { Tabs, Empty, Modal } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { AppContext } from "../../../context/AppContextProvider";
import {
  AddonName,
  DeviceType,
  OrderType,
} from "../../../configs/constants";
import {
  getDiningTables,
  getEmployees,
  getMyProfile,
} from "../../../network/network";
import { handleApiError, showAppError } from "../../../utils/errorHandler";
import { showWarningMessage } from "../../../utils/antdMessage";

import ProductGrid from "./components/ProductGrid";
import CartPanel from "./components/CartPanel";
import EditPriceModal from "./components/EditPriceModal";
import TagOptionsModal from "./components/TagOptionsModal";

import "./POS.css";
import Checkout from "../Checkout/Checkout";

import { getEcho } from "../../../bootstrap/echo";
import { setEchoAuthToken } from "../../../bootstrap/echoAuth";
import {
  getDeviceAuth,
  getDeviceToken,
} from "../../../utils/device/deviceAuth";
import {
  getEntityBranchId,
  isBranchScopedWaiter,
  isEntityInBranch,
} from "../../../utils/branchScope";

const normalizeTagIds = (values) => {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => (value && typeof value === "object" ? value.id : value))
    .filter((value) => value !== undefined && value !== null);
};

const POS = () => {
  const {
    authUser,
    setAuthUser,
    roundOff,
    appConfig,
    fullScreenPOS,
    setFullScreenPOS,
  } = useContext(AppContext);

  const { t } = useTranslation();

  const [productCategoriesList, setProductCategoriesList] = useState([]);
  const [productsList, setProductsList] = useState([]);

  const [cart, setCart] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [activeCategory, setActiveCategory] = useState(0);

  const [editPriceModalVisible, setEditPriceModalVisible] = useState(false);
  const [editPriceModalTitle, setEditPriceModalTitle] = useState("");
  const [editPriceTargetKey, setEditPriceTargetKey] = useState(null);

  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [selectedProductForTags, setSelectedProductForTags] = useState(null);
  const [selectedTagsForModal, setSelectedTagsForModal] = useState([]);

  const [employeesData, setEmployeesData] = useState([]);
  const [diningTablesData, setDiningTablesData] = useState([]);
  const [diningTableId, setDiningTableId] = useState(0);
  const [waiterId, setWaiterId] = useState(0);
  const [loginUserIsWaiter, setLoginUserIsWaiter] = useState(false);

  const [checkoutModalVisible, setCheckoutModalVisible] = useState(false);
  const [checkoutOrderType, setCheckoutOrderType] = useState(null);

  const activeAddons = authUser?.active_addons || [];

  const is_waiter_management_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.WaiterManagement,
  );
  const is_job_management_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.JobManagement,
  );
  const is_table_management_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.TableManagement,
  );
  const is_order_management_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.OrderManagement,
  );
  const is_stock_management_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.Stock,
  );
  const is_ticketing_addon_active = activeAddons.some(
    (addon) => addon.id && addon.name === AddonName.Ticketing,
  );

  const currentBranchId = getEntityBranchId(authUser);

  const branchWaiters = useMemo(
    () =>
      employeesData.filter((employee) =>
        isBranchScopedWaiter(employee, currentBranchId),
      ),
    [employeesData, currentBranchId],
  );

  const readPosDeviceUid = useCallback(
    () => getDeviceAuth(DeviceType.Pos)?.device_uid || null,
    [],
  );

  const [posDeviceUid, setPosDeviceUid] = useState(() => readPosDeviceUid());

  useEffect(() => {
    const sync = () => setPosDeviceUid(readPosDeviceUid());

    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("device-auth-changed", sync);
    window.addEventListener("focus", sync);
    const intervalId = window.setInterval(sync, 1500);

    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("device-auth-changed", sync);
      window.removeEventListener("focus", sync);
      window.clearInterval(intervalId);
    };
  }, [readPosDeviceUid]);

  useEffect(() => {
    getMyProfile()
      .then((response) => {
        if (response?.data?.success === true) {
          const user = response.data.data.user;
          setAuthUser(user);

          const categories = [
            { id: 0, name: t("all"), name_ar: t("all", { lng: "ar" }) },
          ];
          let products = [];

          (user?.categories || []).forEach((category) => {
            if (category?.products?.length > 0) {
              categories.push({
                id: category.id,
                name: category.name,
                name_ar: category.name_ar,
              });
              products = [...products, ...category.products];
            }
          });

          setProductCategoriesList(categories);
          setProductsList(products);

          setLoginUserIsWaiter(!!user?.is_waiter);
          if (user?.is_waiter) setWaiterId(user.id);
        } else if (response?.data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => {
        console.error("Failed to load profile", error);
        handleApiError(error, t);
      });
  }, [t, setAuthUser]);

  useEffect(() => {
    let tax = 0;
    let total = 0;

    cart.forEach((product) => {
      const productSubTotal =
        (Number(product.price) || 0) * (Number(product.quantity) || 0);
      let productTax = 0;

      if (product.is_taxable && product.taxes?.length) {
        product.taxes.forEach((taxItem) => {
          productTax += productSubTotal * (Number(taxItem.rate) / 100);
        });
      }

      tax += productTax;
      total += productSubTotal + productTax;
    });

    setTotalTax(roundOff(tax));
    setTotalAmount(roundOff(total));
  }, [cart, roundOff]);

  const lastWhisperSignatureRef = useRef("");
  const cdsChannelRef = useRef(null);
  const cdsChannelNameRef = useRef(null);
  const cdsSubscribedBoundRef = useRef(false);
  const echoConnBoundRef = useRef(false);
  const lastActiveUserPayloadRef = useRef("");

  const getUserDisplayName = (user) => {
    if (!user) return "";
    return (
      user.name ||
      [user.first_name, user.last_name].filter(Boolean).join(" ") ||
      user.email ||
      ""
    );
  };

  const ensureCdsChannel = useCallback(() => {
    if (!posDeviceUid) return null;

    const token = getDeviceToken(DeviceType.Pos);
    if (!token) return null;

    const echo = getEcho();
    if (!echo || !window.Echo || typeof window.Echo.private !== "function") {
      return null;
    }

    setEchoAuthToken(token);

    const channelName = `terminal.${posDeviceUid}.cds`;

    if (!cdsChannelRef.current || cdsChannelNameRef.current !== channelName) {
      cdsChannelRef.current = window.Echo.private(channelName);
      cdsChannelNameRef.current = channelName;
      cdsSubscribedBoundRef.current = false;
    }

    return cdsChannelRef.current;
  }, [posDeviceUid]);

  const whisperActivePosUser = useCallback(
    (event = "ping") => {
      const channel = ensureCdsChannel();
      if (!channel) return;

      try {
        const payload = {
          name: getUserDisplayName(authUser) || "Unknown",
          event,
          sent_at: Date.now(),
        };

        const sig = JSON.stringify(payload);
        if (lastActiveUserPayloadRef.current === sig) return;

        channel.whisper("active-pos-user", payload);
        lastActiveUserPayloadRef.current = sig;
      } catch {
        // silent
      }
    },
    [authUser, ensureCdsChannel],
  );

  const cartSignature = useMemo(() => {
    const itemsSig = (cart || [])
      .map((it) => {
        const key = it?.cart_key || it?.id || "";
        const qty = Number(it?.quantity || 0);
        const price = Number(it?.price || 0);
        const tagsSig = normalizeTagIds(it?.selectedTagIds)
          .map(String)
          .sort()
          .join(",");
        return `${key}:${qty}:${price}:${tagsSig}`;
      })
      .join("|");

    return `${itemsSig}::total=${Number(totalAmount || 0)}::tax=${Number(
      totalTax || 0,
    )}`;
  }, [cart, totalAmount, totalTax]);

  const mapCartForCds = useCallback((items) => {
    return (Array.isArray(items) ? items : []).map((it) => ({
      cart_key: it?.cart_key || null,
      id: it?.id,
      name: it?.name,
      name_ar: it?.name_ar || it?.nameAr || null,
      name_en: it?.name_en || it?.nameEn || null,
      quantity: Number(it?.quantity || 0),
      price: Number(it?.price || 0),
      tags: Array.isArray(it?.tags) ? it.tags : [],
      selectedTagIds: normalizeTagIds(it?.selectedTagIds),
      notes: it?.notes || null,
    }));
  }, []);

  const mapCartForCheckout = useCallback((items) => {
    return (Array.isArray(items) ? items : []).map((it) => {
      const selectedTagIds = normalizeTagIds(it?.selectedTagIds);

      const selectedTags = Array.isArray(it?.tags)
        ? it.tags.filter((tag) =>
            selectedTagIds.some((id) => Number(id) === Number(tag?.id)),
          )
        : [];

      return {
        ...it,
        cart_key: it?.cart_key || null,
        id: it?.id,
        quantity: Number(it?.quantity || 0),
        price: Number(it?.price || 0),
        selectedTagIds,
        tags: selectedTags,
      };
    });
  }, []);

  useEffect(() => {
    const newName = posDeviceUid ? `terminal.${posDeviceUid}.cds` : null;

    if (cdsChannelNameRef.current && cdsChannelNameRef.current !== newName) {
      try {
        window.Echo?.leave?.(cdsChannelNameRef.current);
      } catch {}

      cdsChannelRef.current = null;
      cdsChannelNameRef.current = null;
    }
  }, [posDeviceUid]);

  useEffect(() => {
    return () => {
      whisperActivePosUser("logout");

      try {
        if (cdsChannelNameRef.current && window.Echo?.leave) {
          window.Echo.leave(cdsChannelNameRef.current);
        }
      } catch {}

      cdsChannelRef.current = null;
      cdsChannelNameRef.current = null;
      cdsSubscribedBoundRef.current = false;
      echoConnBoundRef.current = false;
    };
  }, [whisperActivePosUser]);

  useEffect(() => {
    if (!posDeviceUid) return;

    const token = getDeviceToken(DeviceType.Pos);
    if (!token) return;

    if (lastWhisperSignatureRef.current === cartSignature) return;

    const echo = getEcho();
    if (!echo || !window.Echo || typeof window.Echo.private !== "function") {
      return;
    }

    setEchoAuthToken(token);

    try {
      const channelName = `terminal.${posDeviceUid}.cds`;

      if (!cdsChannelRef.current || cdsChannelNameRef.current !== channelName) {
        cdsChannelRef.current = window.Echo.private(channelName);
        cdsChannelNameRef.current = channelName;
      }

      cdsChannelRef.current.whisper("cart-update", {
        items: mapCartForCds(cart),
        total: totalAmount,
        totalTax,
        status: cart?.length ? "ACTIVE" : "IDLE",
      });

      lastWhisperSignatureRef.current = cartSignature;
    } catch {
      // silent
    }
  }, [posDeviceUid, cart, totalAmount, totalTax, cartSignature, mapCartForCds]);

  useEffect(() => {
    if (!authUser?.id) return;
    whisperActivePosUser("login");
  }, [authUser?.id, whisperActivePosUser]);

  useEffect(() => {
    const handleBeforeUnload = () => whisperActivePosUser("logout");
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [whisperActivePosUser]);

  useEffect(() => {
    if (!posDeviceUid) return;

    const channel = ensureCdsChannel();
    if (!channel) return;

    if (
      !cdsSubscribedBoundRef.current &&
      typeof channel.subscribed === "function"
    ) {
      try {
        channel.subscribed(() => {
          whisperActivePosUser("subscribed");
        });
        cdsSubscribedBoundRef.current = true;
      } catch {}
    }

    const pusher = window.Echo?.connector?.pusher;
    const conn = pusher?.connection;

    if (!conn?.bind || echoConnBoundRef.current) return;

    const onConnected = () => whisperActivePosUser("reconnect");
    const onReconnected = () => whisperActivePosUser("reconnect");

    try {
      conn.bind("connected", onConnected);
      conn.bind("reconnected", onReconnected);
      echoConnBoundRef.current = true;
    } catch {}

    return () => {
      try {
        conn?.unbind?.("connected", onConnected);
        conn?.unbind?.("reconnected", onReconnected);
      } catch {}
      echoConnBoundRef.current = false;
    };
  }, [posDeviceUid, ensureCdsChannel, whisperActivePosUser]);

  useEffect(() => {
    if (!is_job_management_active && !is_waiter_management_active) return;

    getEmployees()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        const owners = response?.data?.data?.owner || [];
        const employees = response?.data?.data?.employees || [];
        setEmployeesData(owners.concat(employees));
      })
      .catch((error) => handleApiError(error, t));
  }, [is_job_management_active, is_waiter_management_active, t]);

  useEffect(() => {
    if (!is_table_management_active) return;

    getDiningTables()
      .then((response) => {
        const data = response?.data || response?.response?.data;
        if (data?.success === true) {
          setDiningTablesData(response?.data?.data?.dining_tables || []);
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  }, [is_table_management_active, t]);

  const getVariantKey = (product) => {
    const sorted = normalizeTagIds(product?.selectedTagIds)
      .map(String)
      .sort()
      .join(",");
    return `${product.id}_${sorted}`;
  };

  const cartReducer = (product, action) => {
    const cartNew = [...cart];
    const incomingVariantKey =
      product.cart_key || getVariantKey(product) || String(product.id);

    const totalQtyForProductId = (productId) =>
      cartNew
        .filter((p) => Number(p.id) === Number(productId))
        .reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);

    const getStock = (p) => (p.is_stockable ? Number(p.stock) || 0 : Infinity);

    if (action === "add") {
      const normalizedTagIds = normalizeTagIds(product.selectedTagIds);

      if (is_stock_management_active && product.is_stockable) {
        const existingQtyTotal = totalQtyForProductId(product.id);
        if (existingQtyTotal >= getStock(product)) {
          showAppError(t("insufficient_quantity"));
          return;
        }
      }

      const indexFound = cartNew.findIndex(
        (p) => p.cart_key === incomingVariantKey,
      );

      if (indexFound === -1) {
        cartNew.push({
          ...product,
          quantity: 1,
          selectedTagIds: normalizedTagIds,
          cart_key: incomingVariantKey,
        });
      } else {
        if (is_stock_management_active && product.is_stockable) {
          const existingQtyTotal = totalQtyForProductId(product.id);
          if (existingQtyTotal >= getStock(product)) {
            showAppError(t("insufficient_quantity"));
            return;
          }
        }

        cartNew[indexFound] = {
          ...cartNew[indexFound],
          quantity: cartNew[indexFound].quantity + 1,
        };
      }
    } else if (action === "inc") {
      const indexFound =
        cartNew.findIndex((p) => p.cart_key === product.cart_key) !== -1
          ? cartNew.findIndex((p) => p.cart_key === product.cart_key)
          : cartNew.findIndex((p) => p.id === product.id);

      if (indexFound !== -1) {
        if (is_stock_management_active && product.is_stockable) {
          const existingQtyTotal = totalQtyForProductId(product.id);
          if (existingQtyTotal >= getStock(product)) {
            showAppError(t("insufficient_quantity"));
            return;
          }
        }

        cartNew[indexFound] = {
          ...cartNew[indexFound],
          quantity: cartNew[indexFound].quantity + 1,
        };
      }
    } else if (action === "dec") {
      const indexFound =
        cartNew.findIndex((p) => p.cart_key === product.cart_key) !== -1
          ? cartNew.findIndex((p) => p.cart_key === product.cart_key)
          : cartNew.findIndex((p) => p.id === product.id);

      if (indexFound !== -1) {
        const newQty = cartNew[indexFound].quantity - 1;
        if (newQty <= 0) {
          cartNew.splice(indexFound, 1);
        } else {
          cartNew[indexFound] = { ...cartNew[indexFound], quantity: newQty };
        }
      }
    } else if (action === "edit_price") {
      const indexFound = product.cart_key
        ? cartNew.findIndex((p) => p.cart_key === product.cart_key)
        : cartNew.findIndex((p) => p.id === product.id);

      if (indexFound !== -1) {
        cartNew[indexFound] = {
          ...cartNew[indexFound],
          price: roundOff(product.price),
        };
      }
    } else if (action === "remove") {
      const indexFound =
        cartNew.findIndex((p) => p.cart_key === product.cart_key) !== -1
          ? cartNew.findIndex((p) => p.cart_key === product.cart_key)
          : cartNew.findIndex((p) => p.id === product.id);

      if (indexFound !== -1) cartNew.splice(indexFound, 1);
    }

    setCart(cartNew);
  };

  const resetTagModal = useCallback(() => {
    setTagModalVisible(false);
    setSelectedProductForTags(null);
    setSelectedTagsForModal([]);
  }, []);

  const handleProductClick = (product) => {
    if (
      is_ticketing_addon_active &&
      is_stock_management_active &&
      product.expiry_date &&
      moment(product.expiry_date, "YYYY-MM-DD").isBefore(
        moment().startOf("day"),
      )
    ) {
      showWarningMessage(t("product_expired"));
      return;
    }

    const stock = product.is_stockable ? Number(product.stock) || 0 : Infinity;
    if (is_stock_management_active && product.is_stockable && stock === 0) {
      showAppError(t("insufficient_quantity"));
      return;
    }

    if (Array.isArray(product.tags) && product.tags.length > 0) {
      setSelectedProductForTags(product);
      setSelectedTagsForModal([]);
      setTagModalVisible(true);
      return;
    }

    cartReducer(product, "add");
  };

  const handleAddToCartFromModal = () => {
    if (!selectedProductForTags) return;

    const productWithTags = {
      ...selectedProductForTags,
      selectedTagIds: normalizeTagIds(selectedTagsForModal),
    };

    cartReducer(productWithTags, "add");
    resetTagModal();
  };

  const openEditPrice = (cart_key) => {
    const target = cart.find((p) => p.cart_key === cart_key);
    if (!target) return;

    setEditPriceTargetKey(cart_key);
    setEditPriceModalTitle(
      `${t("update")} ${t("price")} : ${
        appConfig.direction === "rtl"
          ? target.name || target.name_en || ""
          : target.name_en || target.name || ""
      }`,
    );
    setEditPriceModalVisible(true);
  };

  const handleEditPriceSubmit = (newPrice) => {
    cartReducer(
      { cart_key: editPriceTargetKey, price: newPrice },
      "edit_price",
    );
    setEditPriceModalVisible(false);
    setEditPriceTargetKey(null);
  };

  const showCheckout = (orderType = null) => {
    setCheckoutOrderType(orderType);
    setCheckoutModalVisible(true);
  };

  const handleCheckoutSuccess = () => {
    setCart([]);
    setCheckoutOrderType(null);
  };

  const jobPersonsCount = branchWaiters;
  const tablesCount = diningTablesData.filter((table) =>
    isEntityInBranch(table, currentBranchId),
  );

  const isWaiterAllowedForCurrentBranch = useCallback(
    (selectedWaiterId) => {
      if (!selectedWaiterId) return true;

      return branchWaiters.some(
        (waiter) => String(waiter.id) === String(selectedWaiterId),
      );
    },
    [branchWaiters],
  );

  const handleWaiterChange = useCallback(
    (selectedWaiterId) => {
      const nextWaiterId = selectedWaiterId || 0;

      if (!nextWaiterId) {
        setWaiterId(0);
        return;
      }

      if (!isWaiterAllowedForCurrentBranch(nextWaiterId)) {
        showAppError(t("waiter_not_available_for_branch"));
        return;
      }

      setWaiterId(nextWaiterId);
    },
    [isWaiterAllowedForCurrentBranch, t],
  );

  const selectedWaiter = branchWaiters.find(
    (waiter) => String(waiter.id) === String(waiterId),
  );

  useEffect(() => {
    if (!waiterId || loginUserIsWaiter) return;

    if (!isWaiterAllowedForCurrentBranch(waiterId)) {
      setWaiterId(0);
    }
  }, [waiterId, loginUserIsWaiter, isWaiterAllowedForCurrentBranch]);

  const tabItems = productCategoriesList.map((category) => ({
    key: String(category.id),
    label: appConfig.direction === "rtl" ? category.name_ar : category.name,
    children: (
      <ProductGrid
        productsList={productsList}
        activeCategory={activeCategory}
        appConfig={appConfig}
        t={t}
        authUser={authUser}
        is_stock_management_active={is_stock_management_active}
        is_ticketing_addon_active={is_ticketing_addon_active}
        onProductClick={handleProductClick}
      />
    ),
  }));

  return (
    <>
      {productCategoriesList.length < 2 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 110px)",
          }}
        >
          <Empty />
        </div>
      ) : (
        <>
          <div
            style={{
              overflow: "auto",
              height: fullScreenPOS
                ? "calc(100vh - 40px)"
                : "calc(100vh - 105px)",
              width: "calc(100% - 400px)",
            }}
          >
            <Tabs
              defaultActiveKey="0"
              onChange={(category) => setActiveCategory(Number(category))}
              className="pos_tabs app-pill-tabs"
              items={tabItems}
            />
          </div>

          <CartPanel
            t={t}
            appConfig={appConfig}
            fullScreenPOS={fullScreenPOS}
            setFullScreenPOS={setFullScreenPOS}
            cart={cart}
            authUser={authUser}
            onEditPrice={openEditPrice}
            onCartAction={cartReducer}
            is_stock_management_active={is_stock_management_active}
            roundOff={roundOff}
            totalTax={totalTax}
            totalAmount={totalAmount}
            is_table_management_active={is_table_management_active}
            tablesCount={tablesCount}
            diningTablesData={diningTablesData}
            onDiningTableChange={(value) => setDiningTableId(value || 0)}
            is_waiter_management_active={is_waiter_management_active}
            is_job_management_active={is_job_management_active}
            jobPersonsCount={jobPersonsCount}
            employeesData={employeesData}
            onWaiterChange={handleWaiterChange}
            loginUserIsWaiter={loginUserIsWaiter}
            diningTableId={diningTableId}
            waiterId={waiterId}
            is_order_management_active={is_order_management_active}
            setCart={setCart}
            onCheckout={() => showCheckout(null)}
            onCheckoutTakeaway={() => showCheckout(OrderType.TakeAway)}
          />
        </>
      )}

      <EditPriceModal
        t={t}
        open={editPriceModalVisible}
        title={editPriceModalTitle}
        onClose={() => {
          setEditPriceModalVisible(false);
          setEditPriceTargetKey(null);
        }}
        onSubmit={handleEditPriceSubmit}
      />

      <TagOptionsModal
        t={t}
        appConfig={appConfig}
        open={tagModalVisible}
        onClose={resetTagModal}
        product={selectedProductForTags}
        selected={selectedTagsForModal}
        onChangeSelected={setSelectedTagsForModal}
        onConfirm={handleAddToCartFromModal}
        title={t("select_options")}
        confirmText={selectedTagsForModal.length > 0 ? t("add") : t("skip")}
        cancelText={t("cancel")}
      />

      <Modal
        open={checkoutModalVisible}
        onCancel={() => {
          setCheckoutModalVisible(false);
          setCheckoutOrderType(null);
        }}
        footer={null}
        width="100vw"
        closable={false}
        maskClosable={false}
        style={{
          top: 0,
          padding: 0,
          margin: 0,
          maxWidth: "100vw",
          height: "100vh",
        }}
        styles={{
          body: { padding: "0", minHeight: "calc(100vh - 40px)" },
        }}
        destroyOnClose
      >
        <Checkout
          injectedState={{
            items: mapCartForCheckout(cart),
            diningTableId: diningTableId > 0 ? diningTableId : null,
            waiterId: waiterId > 0 ? waiterId : null,
            waiterBranchId: getEntityBranchId(selectedWaiter),
            branchId: currentBranchId,
            order_type: checkoutOrderType || undefined,
          }}
          onDone={handleCheckoutSuccess}
          onBack={() => {
            setCheckoutModalVisible(false);
            setCheckoutOrderType(null);
          }}
        />
      </Modal>
    </>
  );
};

export default POS;
